import { useInterpret, useSelector } from '@xstate/react'
import { Hit } from 'instantsearch.js'
import uniq from 'lodash/uniq'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { useSearchBox } from 'react-instantsearch'
import { Brand, Category } from 'types/graphql'
import { config } from 'utils/config'
import { useQueryParams } from 'utils/useQueryParams'
import { BrandDiscoveryUrlParam } from 'modules/brands/discovery/utils/buildDiscoveryFilterPath'
import type { ActiveKey, AutocompleteQuerySuggestionsHit, HitsByIndex, IndexHit } from 'modules/brands/list/components/types/Hits'
import { algoliaSearchMachine } from '../controllers/algoliaSearchMachine'
import type { Results } from '../types/Results'
import { useAutocomplete } from './useAutoComplete'

type Index = {
  indexId: string
  indexName: string
  hits?: IndexHit[]
  results: Results
}

export const suggestionClickEvent = 'Search Result Suggestion Clicked'
export const brandClickEvent = 'Search Result Brand Clicked'
export const categoryClickEvent = 'Search Result Category Clicked'

const getHits = (indices: Index[], id: string): any => {
  return uniq(indices.filter((index) => index.indexId === id).flatMap((ix) => ix.hits) || [])
}

const hitsByIndex = (indices: Index[]): HitsByIndex => {
  const suggestionHits = getHits(indices, 'suggestions') as AutocompleteQuerySuggestionsHit<string>[]
  const brandHits = getHits(indices, 'brands') as Hit<Brand>[]
  const categoryHits = getHits(indices, 'categories') as Hit<Category>[]

  return { brandHits, categoryHits, suggestionHits }
}

// Returns unique keys for the search results in the format: [..., 'resultType-resultObjectID']
const resultsKeysFromHits = (hits: HitsByIndex): string[] => {
  const keys = Object.keys(hits).map((key) => hits[key as keyof typeof hits].map((hit) => `${key}-${hit.objectID}`))
  return keys.flat()
}

export const useAlgoliaSearch = () => {
  const { indices, refine } = useAutocomplete()
  const { clear } = useSearchBox()
  const hasResults = indices.some((index) => index.results.nbHits > 0)
  const hits = hitsByIndex(indices as unknown as Index[])
  const resultKeys = resultsKeysFromHits(hits)
  const algoliaSearchService = useInterpret(algoliaSearchMachine, {
    devTools: config.apiEnv !== 'production'
  })
  const { params } = useQueryParams()
  const {
    push,
    query: { categorySlug }
  } = useRouter()

  const urlSearchQuery = params.get(BrandDiscoveryUrlParam.SEARCH_QUERY) || ''
  useEffect(() => {
    algoliaSearchService.send({ type: 'SET_QUERY', query: urlSearchQuery })
  }, [algoliaSearchService, urlSearchQuery])

  useEffect(() => {
    algoliaSearchService.send({ type: 'SET_HITS', hits, hasResults, resultKeys, loading: false, activeKey: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indices])

  const query = useSelector(algoliaSearchService, (state) => state.context.query)
  const isModalOpen = useSelector(algoliaSearchService, (state) => state.context.isModalOpen)
  const inputFocused = useSelector(algoliaSearchService, (state) => state.context.inputFocused)
  const isSearching = useSelector(algoliaSearchService, (state) => state.matches('searching'))
  const isEmpty = useSelector(algoliaSearchService, (state) => state.matches('empty'))
  const isResultsReturned = useSelector(algoliaSearchService, (state) => state.matches('resultsReturned'))
  const isNoResults = useSelector(algoliaSearchService, (state) => state.matches('noResults'))
  const contextHits = useSelector(algoliaSearchService, (state) => state.context.hits)
  const activeKey = useSelector(algoliaSearchService, (state) => state.context.activeKey)
  const loading = useSelector(algoliaSearchService, (state) => state.context.loading)
  const showSuggestions = useSelector(algoliaSearchService, (state) => state.context.showSuggestions)

  const openModal = (showSuggestions?: boolean) => {
    if (!isModalOpen) {
      if (query) {
        refine(query)
      }

      algoliaSearchService.send([
        { type: 'TOGGLE_MODAL', isModalOpen: true },
        { type: 'SET_SHOW_SUGGESTIONS', showSuggestions: !!showSuggestions },
        { type: 'SET_INPUT_FOCUS', inputFocused: true },
        { type: 'SET_ACTIVE_KEY', activeKey: '' }
      ])
    }
  }
  const closeModal = () => {
    if (isModalOpen) {
      algoliaSearchService.send([
        { type: 'TOGGLE_MODAL', isModalOpen: false },
        { type: 'SET_INPUT_FOCUS', inputFocused: false }
      ])
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    const inputValue = event.target.value
    refine(inputValue)

    algoliaSearchService.send([
      { type: 'SET_QUERY', query: inputValue },
      { type: 'SET_LOADING', loading: true }
    ])
  }

  const toggleInputFocus = (inputFocused: boolean) => {
    algoliaSearchService.send({ type: 'SET_INPUT_FOCUS', inputFocused })
  }

  const clearSearch = () => {
    clear()
    algoliaSearchService.send({ type: 'SET_QUERY', query: '' })
  }

  const handleArrowPress = (action: 'increment' | 'decrement') => (e: KeyboardEvent) => {
    e.preventDefault()

    const NULL_INDEX = -1
    const activeKeyIndex = resultKeys.indexOf(activeKey)

    switch (action) {
      case 'increment':
        let prevIndex: number

        // Highlight last item on the first increment OR highlight item above
        prevIndex = activeKeyIndex === NULL_INDEX ? resultKeys.length - 1 : activeKeyIndex - 1

        // Loop to the bottom of the list
        if (activeKeyIndex === 0) {
          prevIndex = resultKeys.length - 1
        }

        algoliaSearchService.send({ type: 'SET_ACTIVE_KEY', activeKey: resultKeys[prevIndex] })
        return
      case 'decrement':
        let nextIndex: number

        // Highlight first item on the first decrement OR highlight item below
        nextIndex = activeKeyIndex === NULL_INDEX ? 0 : activeKeyIndex + 1

        // Loop to the top of the list
        if (activeKeyIndex === resultKeys.length - 1) {
          nextIndex = 0
        }

        algoliaSearchService.send({ type: 'SET_ACTIVE_KEY', activeKey: resultKeys[nextIndex] })
        return

      default:
        break
    }
  }

  const searchForText = (text: string, skipAnalytics?: boolean, global = false) => {
    algoliaSearchService.send([
      { type: 'SET_QUERY', query: text },
      { type: 'SET_LOADING', loading: false },
      { type: 'SET_INPUT_FOCUS', inputFocused: false },
      { type: 'TOGGLE_MODAL', isModalOpen: false }
    ])

    let path = `/brands?q=${text}`

    if (categorySlug && !global) {
      path = `/categories/${categorySlug}/?q=${text}`
    }
    push(path)

    if (skipAnalytics) {
      return
    }
    window.analytics.track('Searched For Text', { query })
  }

  const handleSuggestionClick = (suggestion: string) => {
    window.analytics.track(suggestionClickEvent, { query, suggestion })
    searchForText(suggestion, true)
  }

  const handleCategoryClick = ({ id, slug }: Category) => {
    window.analytics.track(categoryClickEvent, { query, categoryId: id, categorySlug: slug })
    algoliaSearchService.send({ type: 'SET_QUERY', query: '' })
    push(`/categories/${slug}`)
    closeModal()
    clearSearch()
  }

  const handleBrandClick = ({ id, slug }: Brand) => {
    window.analytics.track(brandClickEvent, { query, brandId: id, brandSlug: slug })
    push(`/brands/${slug}/reviews`)
    closeModal()
    clearSearch()
  }

  const handleEnterPress = (e: KeyboardEvent, global = false) => {
    e.preventDefault()

    if (activeKey) {
      const activeKeyType = activeKey.split('-')[0] as ActiveKey
      const activeKeyObjectId: string = activeKey.split('-')[1]

      switch (activeKeyType) {
        case 'suggestionHits':
          const suggestion = hits.suggestionHits.find((hit) => hit.objectID === activeKeyObjectId)?.query

          if (suggestion) handleSuggestionClick(suggestion)
          return
        case 'brandHits':
          const brand = hits.brandHits.find((hit) => hit.objectID === activeKeyObjectId)
          if (brand) handleBrandClick(brand)
          return
        case 'categoryHits':
          const category = hits.categoryHits.find((hit) => hit.objectID === activeKeyObjectId)
          if (category) handleCategoryClick(category)
          return

        // Handle "Enter" key press when no item is highlighted
        default:
          searchForText(query, false, global)
          return
      }
    } else {
      searchForText(query, false, global)
    }
  }

  return {
    openModal,
    closeModal,
    handleInputChange,
    handleCategoryClick,
    handleArrowPress,
    handleEnterPress,
    searchForText,
    clearSearch,
    toggleInputFocus,
    isModalOpen,
    inputFocused,
    query,
    isSearching,
    isEmpty,
    isResultsReturned,
    isNoResults,
    resultKeys,
    activeKey,
    loading,
    showSuggestions,
    ...contextHits
  }
}
